// src/firebase.js
import { initializeApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import {
  browserLocalPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
} from "firebase/auth";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

if (
  !firebaseConfig.apiKey ||
  !firebaseConfig.authDomain ||
  !firebaseConfig.projectId ||
  !firebaseConfig.storageBucket ||
  !firebaseConfig.messagingSenderId ||
  !firebaseConfig.appId
) {
  throw new Error(
    "Missing Firebase env vars. Set all REACT_APP_FIREBASE_* values in .env",
  );
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// App Check — attests that requests come from THIS real web app (not a script
// replaying the public Firebase config) and is enforced on Auth, Firestore, and
// Storage in the console. This is the control that makes the login endpoint and
// database un-scriptable from the outside.
//
// Gated on REACT_APP_FIREBASE_APPCHECK_SITE_KEY (a reCAPTCHA Enterprise key, NOT
// the maps key): with no key set, App Check stays inert so local dev and any
// build made before the console side is configured keep working. Once the key
// is present AND enforcement is turned on in the console, requests without a
// valid App Check token are rejected — so set the key only after registering
// the site, and roll out via the console's monitor-then-enforce flow.
//
// Local dev: set window.FIREBASE_APPCHECK_DEBUG_TOKEN = true (or a registered
// debug-token string) BEFORE init to mint a token reCAPTCHA can't issue on
// localhost. Driven here by REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN so it never
// ships in a production build.
const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY;
if (appCheckSiteKey) {
  const debugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
  if (debugToken) {
    // eslint-disable-next-line no-restricted-globals
    self.FIREBASE_APPCHECK_DEBUG_TOKEN =
      debugToken === "true" ? true : debugToken;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
    // Refresh the App Check token in the background so a long admin session
    // never starts failing reads mid-use once enforcement is on.
    isTokenAutoRefreshEnabled: true,
  });
}

// ✅ Persist the signed-in admin across reloads / deep links.
// initializeAuth defaults to in-memory persistence, which drops the user on
// every page reload — Firestore reads then run unauthenticated and fail the
// security rules. The array gives indexedDB with a localStorage fallback.
const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence],
});
// Initialize Firestore with offline persistence.
// experimentalForceLongPolling: the default streaming (WebChannel) transport is
// blocked/throttled on some networks/proxies, which made admin pages hang
// ~10-30s before falling back. Auto-detect still probes streaming first; force
// long-polling skips that entirely and connects reliably from the first read.
const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
  experimentalForceLongPolling: true,
});
// ✅ Firebase Storage — file/image storage
const storage = getStorage(app);
// Callable Cloud Functions (all deploy to us-central1).
const functions = getFunctions(app, "us-central1");
export { db, auth, storage, app, functions, firebaseConfig };
